import { useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useQuery as useDFCQuery } from 'data-fetching-client';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { createInvoiceServiceRequest } from 'invoices-ui-lib/utils/invoiceService';
import { getPreviewFormUrl } from 'invoices-ui-lib/api/PreviewDomainAPI';
import { FETCH_PREVIEW_DOMAIN } from 'invoices-ui-lib/graphQL/PreviewDomainQueries';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { registerQuery } from 'data-fetching-client';
import { getInvoiceWithAssociatedObjects } from '../api/InvoicesAPI';
import useSignedPreviewUrl from '../hooks/useSignedPreviewUrl';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledIFrame = styled.iframe.withConfig({
  displayName: "InvoicePreviewIframe__StyledIFrame",
  componentId: "roqki7-0"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    border: theme.borderWidth['0'],
    height: '100%',
    width: '100%'
  };
});
export const FETCH_INVOICE_WITH_ASSOCIATED_OBJECTS = registerQuery({
  fieldName: 'invoiceWithAssociatedObjects',
  fetcher({
    id
  }) {
    return getInvoiceWithAssociatedObjects(id);
  }
});
export default function InvoicePreviewIframe({
  invoice,
  shouldFetchInvoice = false,
  isUngatedForInvoicePreview = false
}) {
  const formRef = useRef(null);
  const {
    loading: previewDomainLoading,
    data,
    error
  } = useDFCQuery(FETCH_PREVIEW_DOMAIN);
  const {
    loading: invoiceLoading,
    data: invoiceData,
    error: invoiceError
  } = useDFCQuery(FETCH_INVOICE_WITH_ASSOCIATED_OBJECTS, {
    variables: {
      id: invoice.objectId
    },
    fetchPolicy: 'network-only',
    skip: !shouldFetchInvoice
  });
  const previewDomain = data && data.previewDomain;
  const previewRequestData = useMemo(() => {
    if (shouldFetchInvoice && !invoiceData) {
      return null;
    }
    return invoiceData ? invoiceData.invoiceWithAssociatedObjects : createInvoiceServiceRequest(invoice);
  }, [invoiceData, invoice, shouldFetchInvoice]);
  const {
    signedUrl,
    loading: signedUrlLoading,
    error: signedUrlError,
    canSubmitSignedForm
  } = useSignedPreviewUrl({
    previewDomain,
    previewRequestData,
    isUngatedForInvoicePreview,
    skip: !previewDomain
  });
  const formActionUrl = useMemo(() => {
    if (isUngatedForInvoicePreview) {
      return signedUrl || undefined;
    }
    if (previewDomain) {
      return getPreviewFormUrl(previewDomain);
    }
    return undefined;
  }, [isUngatedForInvoicePreview, signedUrl, previewDomain]);
  const canSubmitForm = useMemo(() => {
    if (shouldFetchInvoice && !invoiceData) {
      return false;
    }
    return isUngatedForInvoicePreview ? canSubmitSignedForm : Boolean(previewDomain);
  }, [previewDomain, invoiceData, shouldFetchInvoice, canSubmitSignedForm, isUngatedForInvoicePreview]);
  useEffect(() => {
    if (formRef.current && canSubmitForm) {
      formRef.current.submit();
    }
  }, [formRef, canSubmitForm]);
  if (previewDomainLoading || invoiceLoading || isUngatedForInvoicePreview && signedUrlLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      layout: "centered",
      minHeight: 150
    });
  } else if (error || invoiceError || isUngatedForInvoicePreview && signedUrlError) {
    return /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoices.components.preview.error"
      })
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("form", {
      "data-test-id": "preview-invoice-form",
      target: "preview-invoice-iframe",
      action: formActionUrl,
      method: "post",
      ref: formRef,
      children: /*#__PURE__*/_jsx("input", {
        "data-test-id": "preview-invoice-input-request",
        type: "hidden",
        name: "previewInvoiceRequest",
        value: JSON.stringify(previewRequestData)
      })
    }), /*#__PURE__*/_jsx(StyledIFrame, {
      name: "preview-invoice-iframe",
      "data-test-id": "preview-invoice-iframe"
    })]
  });
}